/**
 * Who's rooting for who.
 *
 * A badge, the names, the record. Most-supported team first, which in a group
 * this size means the two people who share a team land at the top and everyone
 * else is a row of one — and that is the interesting shape, not a ranking.
 *
 * The names are links to profiles rather than plain text: the whole reason to
 * read this table is to find out who somebody is, and the profile is where
 * their picks and their feats are.
 *
 * `/api/rooting` rather than `/api/state`: it is the last module on the
 * longest screen in the app, nothing above it depends on it, and putting nine
 * more rows into the payload every screen already waits on to make the
 * attendance card appear would be paying for it everywhere.
 */

import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import type { CSSProperties } from 'react'
import type { RootingPage, RootingTeam } from '@shared/types'
import { TEAMS_BY_ID, teamAbbr } from '@shared/teams'
import { GET } from '../lib/api'
import { Eyebrow, SkeletonCard, TeamLogo } from './index'

export function useRooting(): RootingPage | null {
  const [page, setPage] = useState<RootingPage | null>(null)
  useEffect(() => {
    const ac = new AbortController()
    GET<RootingPage>('/api/rooting', ac.signal)
      .then(setPage)
      // Silent. This is a nice-to-have at the foot of the screen and an error
      // notice for it would be louder than the module itself.
      .catch(() => { if (!ac.signal.aborted) setPage(null) })
    return () => ac.abort()
  }, [])
  return page
}

/** One team's row: badge, the people on it, and how the team is doing. */
export function RootingRow({
  teamId, fans, record,
}: {
  teamId: string
  fans: { id: string; displayName: string }[]
  record: string
}) {
  const team = TEAMS_BY_ID[teamId]
  return (
    <div
      style={{
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '11px 2px', borderBottom: '1px solid var(--line)', minHeight: 44,
      }}
    >
      <TeamLogo teamId={teamId} size={30} />
      <div style={{ minWidth: 0, flex: '1 1 auto' }}>
        <div style={{ fontSize: 14, fontWeight: 600, display: 'flex', flexWrap: 'wrap', gap: '0 6px' }}>
          {fans.map((f, i) => (
            <span key={f.id} style={{ display: 'inline-flex' }}>
              <Link to={`/u/${encodeURIComponent(f.id)}`} style={{ color: 'var(--fg)' }}>
                {f.displayName}
              </Link>
              {i < fans.length - 1 ? <span style={{ color: 'var(--dim)' }}>,</span> : null}
            </span>
          ))}
        </div>
        <div className="mono" style={{ fontSize: 11, color: 'var(--dim)', marginTop: 3 }}>
          {team ? `${team.location} ${team.nickname}` : teamAbbr(teamId)}
        </div>
      </div>
      <span className="mono" style={{ fontSize: 13, flex: '0 0 auto' }}>{record}</span>
    </div>
  )
}

/**
 * The two-team version, for a game page: records, then who's on each side.
 *
 * Kept as two tables rather than one because they answer different questions.
 * The records are about the teams and are worth a glance before kickoff; the
 * fans are about the room, and on most games that table is empty — twelve of
 * the sixteen games in a week have nobody in this group invested in them, and
 * a "Fans" heading over nothing said so more loudly than leaving it out does.
 */
export function GameRooting({ sides, style }: { sides: RootingTeam[]; style?: CSSProperties }) {
  if (sides.length < 2) return null
  const withFans = sides.filter((s) => s.fans.length > 0)

  return (
    <div style={style}>
      <Eyebrow>Season records</Eyebrow>
      <div style={{ marginTop: 8 }}>
        {sides.map((s) => {
          const team = TEAMS_BY_ID[s.teamId]
          return (
            <div
              key={s.teamId}
              style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '11px 2px', borderBottom: '1px solid var(--line)', minHeight: 44,
              }}
            >
              <TeamLogo teamId={s.teamId} size={26} />
              <span style={{ fontSize: 14, minWidth: 0, flex: '1 1 auto' }}>
                {team ? `${team.location} ${team.nickname}` : teamAbbr(s.teamId)}
              </span>
              <span className="mono" style={{ fontSize: 13, flex: '0 0 auto' }}>{s.record}</span>
            </div>
          )
        })}
      </div>

      {withFans.length ? (
        <>
          <Eyebrow style={{ marginTop: 22 }}>Fans</Eyebrow>
          <div style={{ marginTop: 8 }}>
            {withFans.map((s) => (
              <RootingRow key={s.teamId} teamId={s.teamId} fans={s.fans} record={s.record} />
            ))}
          </div>
        </>
      ) : null}
    </div>
  )
}

export function RootingTable({ style }: { style?: CSSProperties }) {
  const page = useRooting()

  if (!page) {
    return (
      <section style={style}>
        <Eyebrow large>Who’s rooting for who</Eyebrow>
        <div style={{ marginTop: 10 }}><SkeletonCard lines={3} /></div>
      </section>
    )
  }
  if (!page.teams.length) return null

  return (
    <section style={style}>
      <Eyebrow large>Who’s rooting for who</Eyebrow>
      <div style={{ marginTop: 8 }}>
        {page.teams.map((t) => (
          <RootingRow key={t.teamId} teamId={t.teamId} fans={t.fans} record={t.record} />
        ))}
      </div>
      <div style={{ fontSize: 11, color: 'var(--dim)', marginTop: 8 }}>
        Everyone who’s been here in the last couple of weeks.
      </div>
    </section>
  )
}
